import { z } from 'zod';
import { BeastMcpServer } from './server.js';
import { MetadataRegistry } from './metadata.js';

export interface BeastMcpOptions {
  name: string;
  version: string;
  description: string;
}

export interface ToolOptions {
  title: string;
  description: string;
}

export interface ResourceOptions {
  uri: string;
  title: string;
  description: string;
}

/**
 * BeastMCP-style server that provides a simple, Python-like API
 */
export class BeastMCP {
  private server: BeastMcpServer;
  private metadataRegistry: MetadataRegistry;
  private tools: Map<string, any> = new Map();
  private resources: Map<string, any> = new Map();

  constructor(options: BeastMcpOptions) {
    this.server = new BeastMcpServer();
    this.metadataRegistry = new MetadataRegistry();
  }

  /**
   * Decorator for defining tools - matches Python FastMCP API
   */
  tool(options: ToolOptions) {
    const self = this;
    return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
      const originalMethod = descriptor.value;
      
      // Create zod schema from function parameters
      const paramNames = getParameterNames(originalMethod);
      const zodSchema = createZodSchemaFromParams(paramNames);
      
      descriptor.value = async function (...args: any[]) {
        // Validate input
        const validatedArgs = zodSchema.parse(args[0]);
        
        // Create context
        const context = {
          log: (message: string) => console.log(`[${options.title}] ${message}`),
          reportProgress: (percent: number) => console.log(`[${options.title}] Progress: ${percent}%`),
          getToolId: () => propertyKey,
          getSessionId: () => 'session-' + Date.now(),
          getHostId: () => 'host-' + Date.now(),
          startTimer: () => {},
          endTimer: () => 0,
          getTimers: () => ({}),
          recordMetric: () => {},
          getMetrics: () => ({})
        };

        // Call original method
        const result = originalMethod.apply(this, [validatedArgs, context]);
        
        // Return in MCP format
        return {
          content: [{ type: 'text', text: String(result) }]
        };
      };

      // Register with server
      self.tools.set(propertyKey, {
        name: propertyKey,
        input: zodSchema,
        description: options.description,
        handler: descriptor.value
      });

      return descriptor;
    };
  }

  /**
   * Decorator for defining resources - matches Python FastMCP API
   */
  resource(options: ResourceOptions) {
    const self = this;
    return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
      const originalMethod = descriptor.value;
      
      descriptor.value = async function (...args: any[]) {
        // Create context
        const context = {
          log: (message: string) => console.log(`[${options.title}] ${message}`),
          reportProgress: (percent: number) => console.log(`[${options.title}] Progress: ${percent}%`),
          getToolId: () => propertyKey,
          getSessionId: () => 'session-' + Date.now(),
          getHostId: () => 'host-' + Date.now(),
          startTimer: () => {},
          endTimer: () => 0,
          getTimers: () => ({}),
          recordMetric: () => {},
          getMetrics: () => ({})
        };

        // Call original method
        const result = originalMethod.apply(this, [context]);
        
        // Return in MCP format
        return {
          text: typeof result === 'string' ? result : JSON.stringify(result)
        };
      };

      // Register with server
      self.resources.set(propertyKey, {
        name: propertyKey,
        description: options.description,
        uri: options.uri,
        handler: descriptor.value
      });

      return descriptor;
    };
  }

  /**
   * Start the server
   */
  async start(): Promise<void> {
    // Register all tools and resources with the registry
    const registry = this.server.getRegistry();
    
    for (const [name, tool] of this.tools) {
      registry.registerTool(name, tool, tool.handler);
    }
    
    for (const [name, resource] of this.resources) {
      registry.registerResource(name, resource, resource.handler);
    }

    await this.server.start();
  }

  /**
   * Stop the server
   */
  async stop(): Promise<void> {
    await this.server.stop();
  }
}

/**
 * Extract parameter names from a function
 */
function getParameterNames(func: Function): string[] {
  const funcStr = func.toString();
  const match = funcStr.match(/\(([^)]*)\)/);
  if (!match) return [];
  
  return match[1]
    .split(',')
    .map(param => param.trim())
    .filter(param => param.length > 0);
}

/**
 * Create a basic zod schema from parameter names
 */
function createZodSchemaFromParams(paramNames: string[]): z.ZodSchema {
  const schema: Record<string, z.ZodSchema> = {};
  
  for (const param of paramNames) {
    // Default to string for now - in practice, you'd analyze TypeScript types
    schema[param] = z.any();
  }
  
  return z.object(schema);
}

/**
 * Example usage that matches Python FastMCP simplicity:
 * 
 * ```typescript
 * import { BeastMCP } from 'beastmcp';
 * 
 * // Create an MCP server instance
 * const mcpServer = new BeastMCP({
 *   name: "CalculatorService",
 *   version: "1.0.0",
 *   description: "Provides basic arithmetic operations."
 * });
 * 
 * // Define a tool for addition
 * @mcpServer.tool({
 *   title: "Add Two Numbers",
 *   description: "Adds two numbers together and returns the sum."
 * })
 * function add(a: number, b: number): number {
 *   console.log(`AI requested addition: ${a} + ${b}`);
 *   return a + b;
 * }
 * 
 * // Define a resource
 * @mcpServer.resource({
 *   uri: "system://info",
 *   title: "System Information",
 *   description: "Provides basic information about the server environment."
 * })
 * function getSystemInfo(): Record<string, any> {
 *   return {
 *     os: process.platform,
 *     nodeVersion: process.version
 *   };
 * }
 * 
 * // Start the server
 * mcpServer.start();
 * ```
 */ 